/* =========================================================================
   Shapes — edge routers, as PURE functions (no React).

   Extracted so the app canvas AND the offline screenshot harness draw edges
   with byte-identical geometry — what I tune against a screenshot is exactly
   what ships. Every router has the same signature:

     (a, b, ox, oy) -> SVG path `d`

   where a/b are node centres in layout space and (ox, oy) translate layout
   space into world space (added to every point).
   ========================================================================= */

import { XY } from './layouts';

/** Corner radius used by the rounded routers. */
const R = 14;

/**
 * Build an SVG path through `pts` (layout space) with rounded corners, works
 * for ANY segment angles — orthogonal or 45° diagonal alike, because the
 * rounding is pure vector math on the in/out segment directions.
 */
export function roundedPath(pts: XY[], ox: number, oy: number): string {
  const world = pts
    .filter(
      (p, i, arr) =>
        i === 0 ||
        Math.abs(p.x - arr[i - 1].x) > 0.5 ||
        Math.abs(p.y - arr[i - 1].y) > 0.5
    )
    .map((p) => ({ x: ox + p.x, y: oy + p.y }));

  if (world.length === 0) return '';
  if (world.length === 1) return `M ${world[0].x.toFixed(1)} ${world[0].y.toFixed(1)}`;

  let d = `M ${world[0].x.toFixed(1)} ${world[0].y.toFixed(1)}`;
  for (let i = 1; i < world.length - 1; i += 1) {
    const prev = world[i - 1];
    const p = world[i];
    const next = world[i + 1];
    const inLen = Math.hypot(p.x - prev.x, p.y - prev.y) || 1;
    const outLen = Math.hypot(next.x - p.x, next.y - p.y) || 1;
    const r = Math.min(R, inLen / 2, outLen / 2);
    const ix = p.x - ((p.x - prev.x) / inLen) * r;
    const iy = p.y - ((p.y - prev.y) / inLen) * r;
    const ax = p.x + ((next.x - p.x) / outLen) * r;
    const ay = p.y + ((next.y - p.y) / outLen) * r;
    d += ` L ${ix.toFixed(1)} ${iy.toFixed(1)} Q ${p.x.toFixed(1)} ${p.y.toFixed(
      1
    )} ${ax.toFixed(1)} ${ay.toFixed(1)}`;
  }
  const last = world[world.length - 1];
  d += ` L ${last.x.toFixed(1)} ${last.y.toFixed(1)}`;
  return d;
}

/** Straight connector — used by the radial candidates, where an L-shaped
 *  elbow would fight the geometry. */
export const straightPath = (a: XY, b: XY, ox: number, oy: number): string =>
  `M ${(ox + a.x).toFixed(1)} ${(oy + a.y).toFixed(1)} L ${(ox + b.x).toFixed(1)} ${(
    oy + b.y
  ).toFixed(1)}`;

/** Orthogonal connector (H/V only) with rounded corners; vertical-first, for
 *  the swimlane candidate whose lanes run top-to-bottom. */
export const orthoPath = (a: XY, b: XY, ox: number, oy: number): string => {
  const midY = (a.y + b.y) / 2;
  return roundedPath([a, { x: a.x, y: midY }, { x: b.x, y: midY }, b], ox, oy);
};

/**
 * Octilinear (subway-style) connector: every segment is one of the 8 compass
 * directions — horizontal, vertical, or exactly 45°. Between two points it
 * uses at most one bend: a straight run to absorb the longer axis, then a 45°
 * diagonal into the target. When |dx| == |dy| it is a single clean diagonal;
 * when dx or dy is 0 it is a single H/V line — so grid-aligned rails render as
 * one straight stroke, exactly the subway-map look.
 */
export const octiPath = (a: XY, b: XY, ox: number, oy: number): string => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  const sx = Math.sign(dx);
  const sy = Math.sign(dy);

  // Straight-first, then the 45° diagonal absorbs the shorter axis into b.
  const corner =
    adx > ady
      ? { x: a.x + sx * (adx - ady), y: a.y } // horizontal run, then diagonal
      : { x: a.x, y: a.y + sy * (ady - adx) }; // vertical run, then diagonal

  return roundedPath([a, corner, b], ox, oy);
};
